export interface DomainMission {
  brief: string;
  detailed: string;
  /**
   * ⭐⭐ **THE SUGGESTED OBJECTIVE — the one a user may KNOWINGLY ACCEPT**
   * (owner, 2026-09-10: "the option for the user to have a suggested objective
   * for the domain as a default one... concise, simple and with a human
   * language").
   *
   * ⚠️⚠️ **IT IS A THIRD STRING, NOT A REUSE OF `brief`, AND THAT IS LOAD-BEARING.**
   * `brief` and `detailed` are what a domain's objective reads when NOBODY chose
   * it — the AI fallback used to write `brief` on its own — so
   * `hasAuthoredMission` refuses both, and a domain carrying one never reaches
   * the Build page. If "Use this suggestion" wrote `brief`, the button would put
   * the user in exactly the state that guard exists to catch: an objective on
   * screen, a domain missing from Build, and nothing saying why.
   *
   * ⭐ Written the way the panel asks for it — one short present-tense sentence,
   * first person, plain words. The old two are corporate and none of them are
   * things a person would say out loud.
   */
  suggested: string;
}

export const DEFAULT_DOMAIN_MISSIONS: Record<string, DomainMission> = {
  personal: {
    brief: "To build strength, well-being, and mental clarity through holistic health and fitness.",
    detailed: "To nurture my physical, emotional, and mental well-being through consistent self-care practices. I commit to maintaining my health, building physical strength and resilience, managing stress effectively, and creating the foundation of energy and vitality that supports all other areas of my life.",
    suggested: "I look after my body and mind, so I have the energy everything else needs."
  },
  social: {
    brief: "To cultivate meaningful relationships and contribute positively to my community.",
    detailed: "To build and maintain meaningful connections with family, friends, and community. I strive to be present in my relationships, practice active listening, and create a supportive network that enriches both my life and the lives of others.",
    suggested: "I stay properly in touch with the people who matter, and show up when it counts."
  },
  occupational: {
    brief: "To pursue professional excellence while finding purpose and fulfillment in my work.",
    detailed: "To develop my career with intention and purpose, seeking continuous growth and meaningful contribution. I aim to balance professional achievement with personal well-being, building expertise that serves both my goals and those I work with.",
    suggested: "I get better at the work I do, and do it in a way I can stand behind."
  },
  avocational: {
    brief: "To explore interests and hobbies that bring joy and balance to my life.",
    detailed: "To dedicate time to pursuits that energize and inspire me outside of work. I embrace creative expression, leisure activities, and new experiences that bring balance, reduce stress, and add richness to my daily life.",
    suggested: "I keep making time for the things I do purely because I enjoy them."
  },
  ecological: {
    brief: "To create order through environmental design, finances, possessions, and household management.",
    detailed: "To maintain well-organized physical and digital environments, practice sound financial management, and establish efficient household systems. I commit to managing my spaces, resources, and assets with intention, creating order and clarity that reduces friction and supports focused living.",
    suggested: "I keep my money, my space and my things in order, so they stop getting in the way."
  },
  intellectual: {
    brief: "To continuously learn and grow, expanding my knowledge and mental capabilities.",
    detailed: "To cultivate curiosity and embrace lifelong learning across diverse subjects. I commit to challenging my mind, exploring new ideas, and developing critical thinking skills that enhance my understanding of the world.",
    suggested: "I learn things properly rather than skimming them, and put what I learn to use."
  },
  existential: {
    brief: "To explore meaning, purpose, and spiritual well-being in my life journey.",
    detailed: "To seek deeper understanding of my values, purpose, and place in the world. I commit to regular reflection, mindfulness practices, and exploration of what gives my life meaning and direction.",
    suggested: "I keep asking what actually matters to me, and let the answer change how I live."
  }
};

/**
 * ⚠️ STILL JUST THE TWO. `suggested` is deliberately NOT a length: lengths are
 * interchangeable renderings of the same unauthored placeholder, and
 * `hasAuthoredMission` refuses every one of them. The suggestion is a different
 * KIND of string — one the user can accept — and folding it in here would hand
 * `getDefaultMission` a value the readiness guard must not reject.
 */
export type MissionLength = 'brief' | 'detailed';

export function getDefaultMission(domainId: string, length: MissionLength = 'detailed'): string {
  const mission = DEFAULT_DOMAIN_MISSIONS[domainId.toLowerCase()];
  if (!mission) {
    return length === 'brief'
      ? "To grow and develop in this area of life."
      : "To grow and develop in this area of life, setting meaningful goals and taking consistent action toward improvement.";
  }
  return mission[length];
}

/**
 * The objective offered to a user who has not written one — see `suggested`.
 *
 * ⚠️ An unknown domain gets a generic line rather than an empty string: the
 * panel offers this as a choice, and an empty suggestion card is worse than no
 * card at all.
 */
export function getSuggestedMission(domainId: string): string {
  return DEFAULT_DOMAIN_MISSIONS[domainId.toLowerCase()]?.suggested
    ?? "I give this part of my life real attention, instead of leaving it to drift.";
}
